import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixSiteActionsSiteActionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/site-actions',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/site-actions',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/site-actions',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixSiteV1SitePublisherUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/site-publisher',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/site-publisher',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/site-publisher',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/site-publisher',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_sites_site-actions';

/**
 * Publishes a site.
 *
 * Publishing a site makes any changes previously saved on the site available on the internet.
 * After publishing, changes to your site appear in the [site's history](https://support.wix.com/en/article/viewing-and-managing-your-site-history).
 *
 * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
 */
export function publishSite(payload: object): RequestOptionsFactory<any> {
  function __publishSite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.site_actions.v1.site_actions',
      method: 'POST' as any,
      methodFqn: 'wix.site.v1.SitePublisher.PublishSite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSiteV1SitePublisherUrl({
        protoPath: '/v1/site/publish',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __publishSite;
}

/**
 * Publishes a headless site.
 *
 * Publishing only the headless pages of the site.
 *
 * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
 */
export function deployStandalonePages(
  payload: object
): RequestOptionsFactory<any> {
  function __deployStandalonePages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.site_actions.v1.site_actions',
      method: 'POST' as any,
      methodFqn: 'wix.site.v1.SitePublisher.DeployStandalonePages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSiteV1SitePublisherUrl({
        protoPath: '/v1/site/deploy-standalone-pages',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __deployStandalonePages;
}

/**
 * Duplicates a site with a new site name.
 *
 * > **Note:** When you duplicate a site, some business-related content such as store orders, contacts, invoices, and 3rd-party app settings are not be included.
 * > The duplicated site won’t have a domain or any Premium capabilities.
 *
 * Any installed apps that can be used only on sites with a Premium Plan, will be copied to the duplicated site and
 * will appear unactivated. Once the site is upgraded, the app will be available for use.
 *
 * > **Important:** This call requires an account level API key.
 */
export function duplicateSite(payload: object): RequestOptionsFactory<any> {
  function __duplicateSite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.site_actions.v1.site_actions',
      method: 'POST' as any,
      methodFqn: 'com.wix.site.actions.SiteActionsService.DuplicateSite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixSiteActionsSiteActionsServiceUrl({
        protoPath: '/v1/sites/duplicate',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __duplicateSite;
}

/**
 * This endpoint enables you to delete multiple sites.
 *
 * This is not a permanent delete. Sites are moved to the trash bin and can be restored through site collaborators.
 *
 * Learn more about [deleting multiple sites](https://support.wix.com/en/article/moving-a-site-to-trash).
 *
 * > **Important:** This call requires an account level API key.
 */
export function bulkDeleteSite(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteSite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.site_actions.v1.site_actions',
      method: 'POST' as any,
      methodFqn: 'com.wix.site.actions.SiteActionsService.BulkDeleteSite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixSiteActionsSiteActionsServiceUrl({
        protoPath: '/v1/bulk/sites/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteSite;
}
